// Shared SVG / PNG / PDF export for the rendered Mermaid diagram.
// Previously duplicated in both Online.js and Offline.js.
import { jsPDF } from 'jspdf';
import logger from './logger';

const PADDING = 20;
const MAX_DIMENSION = 16384;
const MAX_PIXELS = 150000000;

const FONT_AWESOME_VERSION = '6.4.0';
const FONT_AWESOME_URLS = [
  `https://cdnjs.cloudflare.com/ajax/libs/font-awesome/${FONT_AWESOME_VERSION}/webfonts/fa-solid-900.woff2`,
  `https://cdnjs.cloudflare.com/ajax/libs/font-awesome/${FONT_AWESOME_VERSION}/webfonts/fa-regular-400.woff2`,
  `https://cdnjs.cloudflare.com/ajax/libs/font-awesome/${FONT_AWESOME_VERSION}/webfonts/fa-brands-400.woff2`
];

const timestampedName = (ext) => `intune-diagram-${Date.now()}.${ext}`;

const triggerDownload = (blobOrUrl, filename) => {
  const url = typeof blobOrUrl === 'string' ? blobOrUrl : URL.createObjectURL(blobOrUrl);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  if (typeof blobOrUrl !== 'string') {
    URL.revokeObjectURL(url);
  }
};

// Pick a raster scale that keeps us inside the browser's canvas limits.
const computeRasterScale = (width, height) => {
  let scale = 1.5;
  if (width > 10000 || height > 10000) scale = 1.0;
  if (width > 20000 || height > 20000) scale = 0.75;
  if (width > 30000 || height > 30000) scale = 0.5;

  const totalPixels = width * scale * height * scale;
  if (width * scale > MAX_DIMENSION || height * scale > MAX_DIMENSION || totalPixels > MAX_PIXELS) {
    const scaleDim = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    const scalePixels = Math.sqrt(MAX_PIXELS / (width * height));
    scale = Math.max(Math.min(scaleDim, scalePixels), 0.3);
    logger.log(`[Export] Adjusted scale to ${scale.toFixed(2)} to fit browser limits`);
  }
  return scale;
};

// Inline every computed style so the serialised SVG renders identically inside
// a canvas, where page stylesheets are not available.
const inlineComputedStyles = (root) => {
  root.querySelectorAll('*').forEach((el) => {
    const computed = window.getComputedStyle(el);
    let styleString = '';
    for (const property of computed) {
      styleString += `${property}:${computed.getPropertyValue(property)};`;
    }
    el.setAttribute('style', styleString);
  });
};

// Fetch the Font Awesome webfonts and inline them as data URIs so exported SVGs
// still show app-type icons when opened outside the browser.
const buildFontFaceRules = async () => {
  await document.fonts.ready;
  let rules = '';

  for (const fontUrl of FONT_AWESOME_URLS) {
    try {
      const response = await fetch(fontUrl);
      const blob = await response.blob();
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });

      let fontFamily = 'Font Awesome 6 Free';
      let fontWeight = '900';
      if (fontUrl.includes('fa-regular')) {
        fontWeight = '400';
      } else if (fontUrl.includes('fa-brands')) {
        fontFamily = 'Font Awesome 6 Brands';
        fontWeight = '400';
      }

      rules += `
        @font-face {
          font-family: "${fontFamily}";
          font-style: normal;
          font-weight: ${fontWeight};
          font-display: block;
          src: url("${dataUrl}") format("woff2");
        }
      `;
    } catch (e) {
      logger.warn('[Export] Failed to load font:', fontUrl, e);
    }
  }

  // Also carry across any Font Awesome class rules already on the page.
  let iconStyles = '';
  for (const sheet of document.styleSheets) {
    try {
      const cssRules = sheet.cssRules || sheet.rules;
      if (!cssRules) continue;
      for (const rule of cssRules) {
        const text = rule.cssText;
        if (!text) continue;
        if ((text.includes('.fa-') || text.includes('Font Awesome')) && !(rule instanceof CSSFontFaceRule)) {
          iconStyles += `${text}\n`;
        }
      }
    } catch (e) {
      // Cross-origin stylesheet - not readable, skip it.
    }
  }

  return rules + '\n' + iconStyles;
};

// Render a prepared SVG string onto a white canvas at the given scale.
const rasterise = (svgString, width, height, scale) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = width * scale;
        canvas.height = height * scale;

        const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: false });
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Failed to load SVG image'));
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
  });

// Clone the live SVG and crop it to its real content bounds.
const cloneCropped = (svgElement) => {
  const bbox = svgElement.getBBox();
  const width = Math.ceil(bbox.width + PADDING * 2);
  const height = Math.ceil(bbox.height + PADDING * 2);
  const viewBoxX = Math.floor(bbox.x - PADDING);
  const viewBoxY = Math.floor(bbox.y - PADDING);

  const clone = svgElement.cloneNode(true);
  clone.removeAttribute('style');
  clone.setAttribute('viewBox', `${viewBoxX} ${viewBoxY} ${width} ${height}`);
  clone.setAttribute('width', width);
  clone.setAttribute('height', height);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  return { clone, width, height };
};

export const exportAsSVG = async (svgElement) => {
  const { clone, width, height } = cloneCropped(svgElement);
  logger.log('[SVG Export] Dimensions:', width, 'x', height);

  try {
    const styles = await buildFontFaceRules();
    if (styles.trim()) {
      const styleElement = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      styleElement.setAttribute('type', 'text/css');
      styleElement.textContent = styles;
      clone.insertBefore(styleElement, clone.firstChild);
    }
  } catch (error) {
    logger.warn('[SVG Export] Failed to embed styles:', error);
  }

  const svgData = new XMLSerializer().serializeToString(clone);
  const blob = new Blob(
    ['<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n' + svgData],
    { type: 'image/svg+xml;charset=utf-8' }
  );
  triggerDownload(blob, timestampedName('svg'));

  return 'SVG exported successfully';
};

export const exportAsPNG = async (svgElement) => {
  const { clone, width, height } = cloneCropped(svgElement);
  clone.style.backgroundColor = 'white';
  inlineComputedStyles(clone);

  const scale = computeRasterScale(width, height);
  logger.log(`[PNG Export] Scale ${scale.toFixed(2)}, ${Math.round(width * scale)}x${Math.round(height * scale)}`);

  const canvas = await rasterise(new XMLSerializer().serializeToString(clone), width, height, scale);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error('Failed to create PNG blob'))),
      'image/png',
      1.0
    );
  });

  triggerDownload(blob, timestampedName('png'));
  return `PNG exported successfully (scale ${scale.toFixed(2)}x)`;
};

export const exportAsPDF = async (svgElement) => {
  const { clone, width, height } = cloneCropped(svgElement);
  clone.style.backgroundColor = 'white';
  inlineComputedStyles(clone);

  const scale = computeRasterScale(width, height);
  logger.log(`[PDF Export] Scale ${scale.toFixed(2)}, ${Math.round(width * scale)}x${Math.round(height * scale)}`);

  const canvas = await rasterise(new XMLSerializer().serializeToString(clone), width, height, scale);

  // Size the page so the raster lands at 300 DPI.
  const dpi = 300;
  const mmPerInch = 25.4;
  const pdfWidth = (canvas.width / dpi) * mmPerInch;
  const pdfHeight = (canvas.height / dpi) * mmPerInch;

  const pdf = new jsPDF({
    orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
    unit: 'mm',
    format: [pdfWidth, pdfHeight],
    compress: false
  });

  pdf.addImage(canvas.toDataURL('image/png', 1.0), 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
  pdf.save(timestampedName('pdf'));

  return 'PDF exported successfully';
};

// Single entry point used by the diagram toolbar.
export const exportDiagram = async (svgElement, format) => {
  if (!svgElement) {
    throw new Error('No diagram to export. Please generate a diagram first.');
  }

  switch (format) {
    case 'svg':
      return exportAsSVG(svgElement);
    case 'png':
      return exportAsPNG(svgElement);
    case 'pdf':
      return exportAsPDF(svgElement);
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
};
